import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../workspace.service';
import * as _ from 'lodash-es';

export class FilterObj {
  filterGroup: any = {};
  toggleSelectedFilter: any[] = [];
  filters: any[] = [];
  filterParams: any = {};
  filterLabels: any = {};
  showFilters = true;
  loading = false;
  loaded = false;
  splitCallParam: string | null = null;

  getFilters(): any[] {
    return this.filters;
  }

  getFilterGroup(): any {
    return this.filterGroup;
  }

  setFilterGroup(fg: any): void {
    this.filterGroup = fg;
  }

  setToggleFilterGroup(filters: any[]): void {
    const filterBy = this.filters.find((f: any) => f?.param_name === 'filter_by');
    const selectedValue = FilterObj.getSelectedFilterValue(filterBy);
    this.toggleSelectedFilter = selectedValue
      ? filters.filter((f: any) => String(f?.filter_key) === selectedValue)
      : [];
  }

  setFilters(f: any[]): void {
    this.filters = f;
    this.setFilterParams();
  }

  setloading(b: boolean): void {
    this.loading = b;
  }

  setLoaded(b: boolean): void {
    this.loaded = b;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  getFilterParams(): any {
    this.filterParams = {};
    this.filterLabels = {};
    this.setFilterParams();
    return this.filterParams;
  }

  getFilterParamsWithLabels(): any {
    this.filterParams = {};
    this.filterLabels = {};
    this.setFilterParams();
    return { ...this.filterParams, ...this.filterLabels };
  }

  setFilterParams(): void {
    _.each(this.filters, (filter: any) => {
      const valueName = filter.value_name || 'VALUE';
      if (filter.selected) {
        if (_.isArray(filter.selected)) {
          if (filter.selected.length) {
            if (_.isObject(filter.selected[0])) {
              const hasTree = filter.selected.some((s: any) => s.children?.length);
              if (hasTree) {
                const keys = FilterObj.collectTreeKeys(filter.selected, valueName);
                const unique = [...new Set(keys.filter(Boolean))];
                this.filterParams[filter.param_name] = unique.join(',');
              } else {
                this.filterParams[filter.param_name] = _.map(
                  filter.selected,
                  (s: any) => s[valueName] ?? s.VALUE ?? s.value ?? s.parnet
                    ?? s.key ?? s.KEY ?? s.mekey ?? ''
                ).filter(Boolean).join(',');
              }
              this.setLabelParam(filter, filter.selected);
            } else {
              this.filterParams[filter.param_name] = filter.selected.join(',');
            }
          }
        } else if (filter.selected[valueName] !== undefined) {
          this.filterParams[filter.param_name] = filter.selected[valueName];
          this.setLabelParam(filter, [filter.selected]);
        }
      }
    });
  }

  private setLabelParam(filter: any, selectedItems: any[]): void {
    if (!filter.param_name?.endsWith('_key')) return;
    const labelParam = filter.param_name.replace(/_key$/, '_label');
    const ln = filter.label_name;
    const labels = selectedItems.map((s: any) => {
      if (ln && s[ln] !== undefined) return s[ln];
      return s?.NAME ?? s?.name ?? s?.label ?? s?.LABEL ?? '';
    }).filter(Boolean);
    if (labels.length) {
      this.filterLabels[labelParam] = labels.join(', ');
    }
  }

  private static collectTreeKeys(nodes: any[], _vn: string): string[] {
    const keys: string[] = [];
    const traverse = (node: any) => {
      const k = node.key ?? node.KEY ?? node.mekey;
      if (k != null) keys.push(String(k));
      if (node.children?.length) {
        node.children.forEach((child: any) => traverse(child));
      }
    };
    nodes.forEach(traverse);
    return keys;
  }

  private static getSelectedFilterValue(filter: any): string {
    if (!filter?.selected) return '';
    const valueName = filter.value_name || 'VALUE';
    const selected = filter.selected;
    if (_.isArray(selected)) {
      const first = selected[0];
      if (_.isObject(first)) {
        const firstObj: any = first;
        return String(firstObj[valueName] ?? firstObj.VALUE ?? firstObj.value ?? firstObj.key ?? firstObj.KEY ?? '');
      }
      return String(first ?? '');
    }
    if (_.isObject(selected)) {
      const selectedObj: any = selected;
      return String(selectedObj[valueName] ?? selectedObj.VALUE ?? selectedObj.value ?? selectedObj.key ?? selectedObj.KEY ?? '');
    }
    return String(selected);
  }

  getFilterParamsFromCache(): any[] {
    const arr: any[] = [];
    _.each(this.filters, (filter: any) => {
      arr.push({
        filter_key: filter.filter_key,
        name: filter.param_name,
        value: filter.selected?.value ?? filter.selected?.VALUE ?? '',
      });
    });
    return arr;
  }

  /**
   * Legacy parity: DataFiltersService.js -> checkRequiredFilters().
   * Returns validity plus the warning messages the UI should surface
   * (one per failed filter, and one combined "A (OR) B ..." message when
   * the OR-group defined via filterGroup.filters_or_group_setup is not
   * satisfied). Mirrors the AngularJS semantics exactly, including the
   * behavior that a satisfied OR-group resets returnVal to true.
   */
  checkRequiredFilters(): { valid: boolean; messages: string[] } {
    const messages: string[] = [];
    let hasRequiredFailure = false;

    const eitherFilter: { label: string; isSelected: boolean }[] = [];
    const either = this.getFilterGroup();
    const checkEitherFilter = either?.filters_or_group_setup;

    let eitherKeys: any[] = [];
    if (checkEitherFilter != null) {
      try {
        eitherKeys = typeof checkEitherFilter === 'string'
          ? JSON.parse(checkEitherFilter)
          : checkEitherFilter;
      } catch {
        eitherKeys = [];
      }
    }

    const eitherKeySet = new Set(
      (eitherKeys || []).map((k: any) => String(k))
    );

    _.each(this.filters, (filter: any) => {
      if (eitherKeySet.has(String(filter.filter_key))) {
        const unselected = typeof filter.selected === 'undefined'
          || _.isEmpty(filter.selected)
          || (_.isArray(filter.selected) && filter.selected.length <= 0);
        eitherFilter.push({ label: filter.label, isSelected: !unselected });
        return;
      }

      if (Number(filter.required) !== 1) return;

      if (Number(filter.multiple) === 1) {
        if (
          typeof filter.selected === 'undefined'
          || _.isEmpty(filter.selected)
          || (_.isArray(filter.selected) && filter.selected.length <= 0)
        ) {
          messages.push(`${filter.label} is required. Please select a value`);
          hasRequiredFailure = true;
        }
      } else if (
        typeof filter.selected === 'undefined'
        || _.isEmpty(filter.selected)
        || filter.selected?.[filter.value_name] === ''
        || (_.isArray(filter.selected) && filter.selected.length === 0)
      ) {
        messages.push(`${filter.label} is required. Please select a value`);
        hasRequiredFailure = true;
      }
    });

    let orGroupFailed = false;
    if (eitherFilter.length > 0) {
      if (!eitherFilter.some((i) => i.isSelected)) {
        const combinedLabel = eitherFilter.map((it) => it.label).join(' (OR) ');
        messages.push(`${combinedLabel} is required. Please select a value`);
        orGroupFailed = true;
      }
    }

    return { valid: !hasRequiredFailure && !orGroupFailed, messages };
  }
}

/**
 * Filter groups whose static `tax_year` dropdown must follow
 * `globalParams.tax_year` after `/getFilters` reload (not always `values[0]`).
 *
 * Issue: Foreign GILTI/BH 2018 Exam grids (filter group "Tax Year 2017-18").
 * Changing the bar year to 2018 triggered reload but UI reverted to 2017.
 */
const FILTER_GROUP_KEYS_STATIC_TAX_YEAR_MATCH_GLOBAL: ReadonlySet<string> = new Set([
  '2528',
]);

@Injectable({ providedIn: 'root' })
export class DataFiltersService {
  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private workspaceService: WorkspaceService
  ) {}

  /** Backend varies: `callSuccess` is `'1'`, `1`, or occasionally boolean. */
  private static isCallSuccess(data: any): boolean {
    const v = data?.callSuccess;
    return v === '1' || v === 1 || v === true || String(v).toLowerCase() === 'true';
  }

  /**
   * Legacy parity with `JsonObjectFactory.getJSONObj` (AngularJS `GlobalServices.js` ~L2003):
   * GET `/getFilters` receives `_.merge({}, GlobalService.globalParams, _.merge({}, filterParams, { filter_group_key }))`,
   * minus `checkLockedbj`, plus the same scenario backfill when `filterParams.scenario` is missing.
   */
  private buildGetFiltersHttpParams(filterGroupKey: string): { [param: string]: string } {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    let filterParams: any = {};
    try {
      if (activeScreen?.filters && typeof activeScreen.filters.getFilterParams === 'function') {
        filterParams = activeScreen.filters.getFilterParams() || {};
      }
    } catch {
      filterParams = {};
    }

    const paramsMerge = _.merge({}, filterParams, { filter_group_key: filterGroupKey });
    const merged: any = _.merge({}, this.globalService.global?.globalParams || {}, paramsMerge);
    delete merged.checkLockedbj;

    const ty =
      merged.tax_year ??
      filterParams.tax_year ??
      this.globalService.global?.globalParams?.tax_year;
    if (ty != null && String(ty).length > 0) {
      if (filterParams.scenario === undefined || filterParams.scenario === null) {
        const fromGlobal = this.globalService.getScenario(ty);
        if (
          fromGlobal != null &&
          (merged.scenario === undefined || merged.scenario === null)
        ) {
          merged.scenario = fromGlobal;
        }
      }
    }

    const out: { [param: string]: string } = {};
    for (const key of Object.keys(merged)) {
      if (key === 'checkLockedbj') continue;
      const v = merged[key];
      if (v === undefined || v === null) continue;
      if (typeof v === 'object') continue;
      out[key] = String(v);
    }
    out.filter_group_key = String(filterGroupKey);
    return out;
  }

  loadFilters(filterKey: string): Promise<FilterObj> {
    const filterObj = new FilterObj();
    filterObj.setloading(true);

    const url = `${environment.base_url}/getFilters`;
    return firstValueFrom(
      this.http.get<any>(url, { params: this.buildGetFiltersHttpParams(filterKey) })
    )
      .then((data: any) => {
        if (!DataFiltersService.isCallSuccess(data)) {
          filterObj.setloading(false);
          filterObj.setLoaded(true);
          return filterObj;
        }

        // Defensive: the backend can return `{ callSuccess: '1', filterGroup: null }`
        // for screens whose filter_group_key has no rows in the current
        // tax_year / scenario / jcd_key context (or when the screen is
        // legitimately filter-less but still has a filter_key wired up).
        // Without this guard `data.filterGroup.filters` below would throw a
        // TypeError and the `.catch` branch would mark `loaded=true` — same
        // observable outcome, but logged as an exception every time. Bail
        // cleanly with an empty FilterObj instead.
        const filterGroup = data.filterGroup ?? { filters: [] };
        const groupFilters: any[] = Array.isArray(filterGroup.filters)
          ? filterGroup.filters
          : [];

        // Backend stores ui_type / data_type with trailing whitespace (e.g. "df-select-multiple\n").
        // AngularJS trimmed at every comparison; we normalize once at entry for service+component parity.
        for (const f of groupFilters) {
          if (typeof f.ui_type === 'string') f.ui_type = f.ui_type.trim();
          if (typeof f.data_type === 'string') f.data_type = f.data_type.trim();
        }

        this.globalService.setFilterGroup(groupFilters);
        filterObj.setFilterGroup(filterGroup);
        return this.resolveFilterValues(filterGroup, filterObj).then(() => {
          filterObj.setFilters(groupFilters);
          filterObj.setloading(false);
          filterObj.setLoaded(true);
          return filterObj;
        });
      })
      .catch(() => {
        // Same rationale as the callSuccess !== '1' branch above: mark
        // loaded so the grid stops bailing and the user sees a stable
        // (empty) state instead of an infinite "filters not loaded".
        filterObj.setloading(false);
        filterObj.setLoaded(true);
        return filterObj;
      });
  }

  private findCommentLinkValue(commentFilters: any[], filter: any): any {
    if (!Array.isArray(commentFilters) || !commentFilters.length) return null;
    const targetFilterKey = String(filter?.filter_key ?? '');
    const targetParamName = String(filter?.param_name ?? '').toLowerCase();

    return commentFilters.find((item: any) => String(item?.filter_key ?? '') === targetFilterKey)
      || commentFilters.find((item: any) => String(item?.name ?? '').toLowerCase() === targetParamName)
      || commentFilters.find((item: any) => String(item?.predicate ?? '').toLowerCase() === targetParamName)
      || null;
  }

  private getCommentLinkRawValue(copyVal: any, filter: any): any {
    if (!copyVal) return undefined;
    const valueName = filter?.value_name || 'VALUE';
    return copyVal[valueName]
      ?? copyVal[String(valueName).toLowerCase()]
      ?? copyVal.value
      ?? copyVal.parnet
      ?? copyVal.key
      ?? copyVal.KEY
      ?? copyVal.mekey
      ?? copyVal.VALUE;
  }

  private applyCommentLinkValue(filter: any, copyVal: any): boolean {
    if (!copyVal || !filter?.values?.length) {
      console.log('[DataFiltersService] applyCommentLinkValue bail: no copyVal or values empty, param:', filter?.param_name, 'values:', filter?.values?.length);
      return false;
    }

    const valueName = filter.value_name || 'VALUE';
    const rawValue = this.getCommentLinkRawValue(copyVal, filter);
    console.log('[DataFiltersService] rawValue type:', typeof rawValue, 'length:', String(rawValue ?? '').length, 'first 100 chars:', String(rawValue ?? '').substring(0, 100));
    if (rawValue === undefined || rawValue === null || String(rawValue).length === 0) {
      return false;
    }

    if (filter.multiple === 1 || this.isMultiType(filter)) {
      const parsedValues = Array.isArray(rawValue)
        ? rawValue.map((value: any) => String(value))
        : String(rawValue ?? '')
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);

      const collectMatching = (items: any[]): any[] => {
        const matched: any[] = [];
        for (const item of items) {
          const iv = String(
            item?.[valueName] ?? item?.VALUE ?? item?.value ?? item?.parnet ?? item?.key ?? item?.KEY ?? item?.mekey ?? ''
          );
          if (parsedValues.includes(iv)) {
            matched.push(item);
          }
          if (item?.children?.length) {
            matched.push(...collectMatching(item.children));
          }
        }
        return matched;
      };

      const nextSelected = collectMatching(filter.values);
      console.log('[DataFiltersService] multi-select match:', filter.param_name, 'parsedValues count:', parsedValues.length, 'matched count:', nextSelected.length, 'filter.values top-level:', filter.values.length, 'valueName:', valueName);
      if (nextSelected.length === 0 && parsedValues.length > 0) {
        const sample = filter.values.slice(0, 3).map((v: any) => ({ [valueName]: v?.[valueName], VALUE: v?.VALUE, key: v?.key, KEY: v?.KEY, mekey: v?.mekey, hasChildren: !!v?.children?.length }));
        console.log('[DataFiltersService] NO MATCH! sample values:', sample, 'sample parsedValues:', parsedValues.slice(0, 5));
      }

      const currentSelected = Array.isArray(filter.selected) ? filter.selected : [];
      const currentKey = currentSelected
        .map((item: any) => String(item?.[valueName] ?? item?.VALUE ?? item?.value ?? item?.parnet ?? item?.key ?? item?.KEY ?? item?.mekey ?? ''))
        .sort()
        .join(',');
      const nextKey = nextSelected
        .map((item: any) => String(item?.[valueName] ?? item?.VALUE ?? item?.value ?? item?.parnet ?? item?.key ?? item?.KEY ?? item?.mekey ?? ''))
        .sort()
        .join(',');

      if (currentKey !== nextKey) {
        filter.selected = nextSelected;
        return true;
      }
      return false;
    }

    const nextSelected = filter.values.find((item: any) =>
      String(item?.[valueName] ?? item?.VALUE ?? item?.value ?? '') === String(rawValue ?? '')
    );

    const currentValue = filter.selected
      ? String(filter.selected?.[valueName] ?? filter.selected?.VALUE ?? filter.selected?.value ?? '')
      : '';
    const nextValue = nextSelected
      ? String(nextSelected?.[valueName] ?? nextSelected?.VALUE ?? nextSelected?.value ?? '')
      : '';

    if (currentValue !== nextValue) {
      filter.selected = nextSelected ?? null;
      return true;
    }

    return false;
  }

  async applyCommentLinkFilters(filterObj: FilterObj, commentFilters: any[]): Promise<boolean> {
    const filters = filterObj?.filters || [];
    if (!filters.length || !Array.isArray(commentFilters) || !commentFilters.length) {
      return false;
    }

    const changedKeys: string[] = [];

    console.log('[DataFiltersService] applyCommentLinkFilters: filters count:', filters.length, 'commentFilters count:', commentFilters.length);
    console.log('[DataFiltersService] commentFilters:', commentFilters.map((f: any) => ({ name: f.name, filter_key: f.filter_key, valueLen: String(f.value || '').length })));

    for (const filter of filters) {
      const copyVal = this.findCommentLinkValue(commentFilters, filter);
      if (!copyVal) continue;

      console.log('[DataFiltersService] matched filter:', filter.param_name, 'filter_key:', filter.filter_key, 'ui_type:', filter.ui_type, 'multiple:', filter.multiple, 'values count:', filter.values?.length, 'has children:', filter.values?.[0]?.children?.length > 0);

      const changed = this.applyCommentLinkValue(filter, copyVal);
      console.log('[DataFiltersService] applyCommentLinkValue result:', changed, 'param:', filter.param_name, 'selected count:', Array.isArray(filter.selected) ? filter.selected.length : (filter.selected ? 1 : 0));
      if (changed) {
        changedKeys.push(String(filter.filter_key));
      }
    }

    for (const changedKey of changedKeys) {
      const changedFilter = filters.find((filter: any) => String(filter?.filter_key ?? '') === changedKey);
      if (changedFilter) {
        await this.cascadeByRefreshList(changedFilter, filterObj, commentFilters);
      }
    }

    return changedKeys.length > 0;
  }

  private usesStaticTaxYearGlobalMatch(filterGroupKey: string | number | undefined | null): boolean {
    const key = String(filterGroupKey ?? '').trim();
    return key.length > 0 && FILTER_GROUP_KEYS_STATIC_TAX_YEAR_MATCH_GLOBAL.has(key);
  }

  private async resolveFilterValues(filterGroup: any, filterObj: FilterObj): Promise<void> {
    // Keep FilterObj.filters in sync while we mutate filterGroup.filters so
    // cascadeByRefreshList can run after defaulting `filter_by`.
    filterObj.setFilters(filterGroup.filters);

    const globalInputs = this.globalService.global?.inputs || {};
    const globalParams = this.globalService.global?.globalParams || {};
    const params: any = {};

    if (!globalParams.tax_year && globalInputs.tax_year?.selected?.value) {
      globalParams.tax_year = globalInputs.tax_year.selected.value;
      globalParams.scenario = globalInputs.tax_year.selected.scenario;
    }

    const loadFilters: any[] = [];

    for (const filter of filterGroup.filters) {
      if (filter.data_type === 'inherit') {
        const input = globalInputs[filter.param_name];
        if (input?.values) {
          filter.values = _.clone(input.values);
          const vn = filter.value_name;
          const currentVal = globalParams[filter.param_name];

          if (currentVal != null && currentVal !== '') {
            filter.selected = _.find(filter.values, (v: any) =>
              String(v[vn]) === String(currentVal) ||
              String(v.VALUE ?? '') === String(currentVal) ||
              String(v.value ?? '') === String(currentVal)
            );
          }

          if (!filter.selected && input.selected && !_.isEmpty(input.selected)) {
            filter.selected = _.find(filter.values, (v: any) =>
              String(v[vn]) === String(input.selected[vn]) ||
              String(v[vn]) === String(input.selected.VALUE ?? '') ||
              String(v[vn]) === String(input.selected.value ?? '') ||
              String(v.value ?? '') === String(input.selected.value ?? '')
            ) || input.selected;
          }

          if (filter.selected) {
            params[filter.param_name] = filter.selected[vn]
              ?? filter.selected.value ?? filter.selected.VALUE;
          }
        }
      } else if (filter.data_type === 'static' && filter.static_values) {
        try {
          filter.values = JSON.parse(filter.static_values);
          if (
            filter.param_name === 'tax_year'
            && this.usesStaticTaxYearGlobalMatch(filterGroup.filter_group_key)
          ) {
            this.autoSelectFilterValue(filter);
          } else if (filter.default_value) {
            this.applyStaticDefaultValue(filter);
          } else {
            filter.selected = filter.values[0];
          }
          if (filter.selected) {
            params[filter.param_name] = filter.selected.VALUE ?? filter.selected.value;
          }
        } catch (_) { /* ignore parse errors */ }
      } else if (filter.data_type === 'text' || filter.ui_type === 'df-text') {
        this.initializeTextFilter(filter);
        if (filter.selected) {
          const vn = filter.value_name || 'VALUE';
          const value = filter.selected[vn] ?? filter.selected.VALUE ?? filter.selected.value ?? '';
          if (value !== undefined && value !== null && String(value) !== '') {
            params[filter.param_name] = value;
          }
        }
      } else if (filter.data_type === 'load') {
        loadFilters.push(filter);
      }
    }

    const requestParams = () =>
      _.merge({}, params, this.buildCurrentFilterParams(filterGroup.filters));

    for (const filter of loadFilters) {
      if (!this.canLoadFilterFromDependencies(filter, filterGroup.filters)) {
        filter.values = [];
        filter.selected = this.isMultiType(filter) ? [] : null;
        (filter as any)._skippedForDeps = true;
        continue;
      }
      (filter as any)._skippedForDeps = false;
      await this.loadFilterValues(filter, filterGroup.filter_group_key, requestParams());
    }

    // If ME (or another URL loader) was skipped because a required dependency
    // had no selection yet, but that dependency was filled by a later filter
    // in the same group, reload skipped rows once.
    for (const filter of loadFilters) {
      if (!(filter as any)._skippedForDeps) continue;
      if (!this.canLoadFilterFromDependencies(filter, filterGroup.filters)) continue;
      await this.loadFilterValues(filter, filterGroup.filter_group_key, requestParams());
    }

    for (const filter of loadFilters) {
      delete (filter as any)._skippedForDeps;
    }

    // OR-branch filters (is_or_conditional_filter) are shown only when `filter_by`
    // matches (data-filters isHiddenByFilterBy). If `filter_by` never got a
    // selection (deps / order / API), every OR branch stayed hidden — user saw
    // only year/status/entity group (screen2) instead of ME or filing group (screen1).
    // Legacy behavior: default the first `filter_by` option, then cascade dependents.
    const fb = filterGroup.filters.find((f: any) => f.param_name === 'filter_by');
    if (fb && !this.hasSelection(fb) && fb.values?.length && !this.isMultiType(fb)) {
      fb.selected = fb.values[0];
      filterObj.setFilters(filterGroup.filters);
      await this.cascadeByRefreshList(fb, filterObj, undefined);
    }
  }

  private static readonly MULTI_UI_TYPES = [
    'df-me-drop-down', 'df-select-multiple',
    'df-select-virtual-scroll', 'df-select-single'
  ];

  private isMultiType(filter: any): boolean {
    return filter.multiple === 1 || DataFiltersService.MULTI_UI_TYPES.includes(filter.ui_type);
  }

  private hasSelection(filter: any): boolean {
    if (!filter) return false;
    if (typeof filter.selected === 'undefined' || filter.selected === null) return false;
    if (_.isArray(filter.selected)) return filter.selected.length > 0;
    if (_.isObject(filter.selected)) {
      const vn = filter.value_name || 'VALUE';
      const value = filter.selected[vn] ?? filter.selected.VALUE ?? filter.selected.value;
      return value !== undefined && value !== null && String(value) !== '';
    }
    return String(filter.selected) !== '';
  }

  private canLoadFilterFromDependencies(filter: any, filters: any[]): boolean {
    const refreshList = this.getRefreshList(filter);
    if (!refreshList.length) return true;

    for (const depKey of refreshList) {
      const depFilter = filters.find((f: any) => String(f?.filter_key ?? '').trim() === depKey);
      if (Number(depFilter?.required) === 1 && !this.hasSelection(depFilter)) {
        return false;
      }
    }

    return true;
  }

  private async loadFilterValues(filter: any, groupKey: string, params: any): Promise<void> {
    if (!filter.data_url) return;

    const url = `${environment.base_url}/${filter.data_url}`;
    filter.loading = true;

    const globalParams = this.globalService.global?.globalParams || {};
    const mergedParams = _.merge({}, globalParams, params);

    try {
      const data: any = await firstValueFrom(this.http.get(url, { params: mergedParams }));
      if (DataFiltersService.isCallSuccess(data)) {
        const rows = data?.jsonObject ?? data?.json_object;
        filter.values = Array.isArray(rows) ? rows : [];

        if (filter.default_value) {
          try {
            filter.values.unshift(JSON.parse(filter.default_value));
          } catch (_) { /* ignore */ }
        }

        if (this.isMultiType(filter)) {
          if (!_.isArray(filter.selected)) filter.selected = [];
        } else {
          if (!filter.selected && filter.values.length) {
            this.autoSelectFilterValue(filter);
          }
          if (filter.selected) {
            params[filter.param_name] = filter.selected.VALUE ?? filter.selected.value;
          }
        }
      }
    } catch (e) {
      console.warn(`[DataFilters] Failed to load filter "${filter.label}":`, e);
    }
    filter.loading = false;
  }

  private applyStaticDefaultValue(filter: any): void {
    if (!filter.values?.length) {
      filter.selected = null;
      return;
    }

    const vn = filter.value_name || 'VALUE';
    try {
      const def = JSON.parse(filter.default_value);
      const defValue = def?.[vn] ?? def?.VALUE ?? def?.value;
      const match = filter.values.find((v: any) =>
        String(v?.[vn] ?? v?.VALUE ?? v?.value ?? '') === String(defValue ?? '')
      );
      filter.selected = match || filter.values[0];
    } catch {
      filter.selected = filter.values[0];
    }
  }

  private initializeTextFilter(filter: any): void {
    let initial = '';
    if (filter.default_value) {
      try {
        const parsed = JSON.parse(filter.default_value);
        if (typeof parsed === 'string') {
          initial = parsed;
        } else if (parsed && typeof parsed === 'object') {
          const vn = filter.value_name || 'VALUE';
          initial = String(parsed[vn] ?? parsed.VALUE ?? parsed.value ?? '');
        }
      } catch {
        initial = String(filter.default_value).replace(/^"|"$/g, '');
      }
    }

    const vn = filter.value_name || 'VALUE';
    filter.selected = { VALUE: initial };
    if (vn !== 'VALUE') {
      filter.selected[vn] = initial;
    }
  }

  private autoSelectFilterValue(filter: any): void {
    if (!filter.values?.length) return;

    const globalParams = this.globalService.global?.globalParams || {};
    const globalInputs = this.globalService.global?.inputs || {};

    if (filter.param_name === 'tax_year') {
      const taxYear = globalParams.tax_year
        || globalInputs.tax_year?.selected?.value;
      if (taxYear) {
        const vn = filter.value_name || 'VALUE';
        const match = filter.values.find(
          (v: any) =>
            String(v[vn]) === String(taxYear) ||
            String(v.VALUE ?? '') === String(taxYear) ||
            String(v.value ?? '') === String(taxYear) ||
            String(v.label ?? '') === String(taxYear)
        );
        if (match) {
          filter.selected = match;
          return;
        }
      }
    }

    if (filter.param_name === 'scenario') {
      const taxYear = globalParams.tax_year;
      if (taxYear) {
        const scenarioVal = this.globalService.getScenario(taxYear);
        if (scenarioVal != null) {
          const match = filter.values.find(
            (i: any) => parseInt(i.VALUE, 10) === parseInt(scenarioVal, 10)
          );
          if (match) {
            filter.selected = match;
            return;
          }
        }
      }
    }

    filter.selected = filter.values[0];
  }

  private buildCurrentFilterParams(filters: any[]): any {
    const params: any = {};
    for (const filter of filters) {
      if (!filter.selected) continue;
      const vn = filter.value_name || 'VALUE';
      if (_.isArray(filter.selected)) {
        if (filter.selected.length) {
          if (_.isObject(filter.selected[0])) {
            const vals = filter.selected
              .map((s: any) => s[vn] ?? s.VALUE ?? s.value ?? s.parnet ?? s.key ?? s.KEY ?? '')
              .filter(Boolean);
            if (vals.length) params[filter.param_name] = vals.join(',');
          } else {
            params[filter.param_name] = filter.selected.join(',');
          }
        }
      } else if (filter.selected[vn] !== undefined) {
        params[filter.param_name] = filter.selected[vn];
      }
    }
    return params;
  }

  private getRefreshList(filter: any): string[] {
    if (!filter?.refresh_list) return [];
    return String(filter.refresh_list)
      .split(',')
      .map((k) => String(k).trim())
      .filter((k) => k.length > 0);
  }

  private dependsOnFilterKey(filter: any, changedFilterKey: string): boolean {
    if (!changedFilterKey) return false;
    const refreshList = this.getRefreshList(filter);
    return refreshList.includes(String(changedFilterKey));
  }

  hasDependentFilters(changedFilter: any, filterObj: FilterObj): boolean {
    const changedKey = String(changedFilter?.filter_key ?? '').trim();
    if (!changedKey) return false;

    return (filterObj.filters || []).some(
      (filter: any) =>
        filter.data_type === 'load' && this.dependsOnFilterKey(filter, changedKey)
    );
  }

  async cascadeByRefreshList(changedFilter: any, filterObj: FilterObj, commentFilters?: any[]): Promise<void> {
    const filters = filterObj.filters;
    const groupKey = filterObj.filterGroup?.filter_group_key || '';
    const changedKey = String(changedFilter?.filter_key ?? '').trim();

    if (!changedKey || !filters?.length) return;

    const sourceQueue: string[] = [changedKey];
    const refreshedKeys = new Set<string>([changedKey]);

    while (sourceQueue.length) {
      const currentSources = [...sourceQueue];
      sourceQueue.length = 0;

      const dependentFilters = filters.filter((filter: any) => {
        if (filter.data_type !== 'load') return false;
        const filterKey = String(filter?.filter_key ?? '').trim();
        if (!filterKey || refreshedKeys.has(filterKey)) return false;
        return currentSources.some((sourceKey) => this.dependsOnFilterKey(filter, sourceKey));
      });

      if (!dependentFilters.length) {
        continue;
      }

      const nextSourceKeys: string[] = [];
      const deferredEntries: { filter: any; filterKey: string }[] = [];

      for (const filter of dependentFilters) {
        const filterKey = String(filter?.filter_key ?? '').trim();
        if (!filterKey) continue;

        filter.loading = true;
        if (this.isMultiType(filter)) {
          filter.selected = [];
        } else {
          filter.selected = null;
        }

        if (!this.canLoadFilterFromDependencies(filter, filters)) {
          deferredEntries.push({ filter, filterKey });
          continue;
        }

        const params = this.buildCurrentFilterParams(filters);
        await this.loadFilterValues(filter, groupKey, params);
        if (Array.isArray(commentFilters) && commentFilters.length) {
          const copyVal = this.findCommentLinkValue(commentFilters, filter);
          if (copyVal) {
            this.applyCommentLinkValue(filter, copyVal);
          }
        }
        refreshedKeys.add(filterKey);
        nextSourceKeys.push(filterKey);
      }

      for (const { filter, filterKey } of deferredEntries) {
        if (!this.canLoadFilterFromDependencies(filter, filters)) {
          filter.values = [];
          filter.loading = false;
          continue;
        }

        const params = this.buildCurrentFilterParams(filters);
        await this.loadFilterValues(filter, groupKey, params);
        if (Array.isArray(commentFilters) && commentFilters.length) {
          const copyVal = this.findCommentLinkValue(commentFilters, filter);
          if (copyVal) {
            this.applyCommentLinkValue(filter, copyVal);
          }
        }
        refreshedKeys.add(filterKey);
        nextSourceKeys.push(filterKey);
      }

      sourceQueue.push(...nextSourceKeys);
    }
  }
}
